import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPinned } from "lucide-react";
import { useStore } from "@tanstack/react-form";
import { Button } from "components/common/Button";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { ElementsListViewV3 } from "./ElementsListViewV3";
import { CreationModeActionBar } from "./CreationModeActionBar";
import { GroupSelectionStepV3 } from "./GroupSelectionStepV3";
import type { ElementsCreationModeV3Props } from "./types";

export const ElementsCreationModeV3 = ({
  creationModeState,
  isMultiAddModeEnabled,
  onToggleMultiAddMode,
  isEditingElement,
  onToggleEditMode,
}: ElementsCreationModeV3Props) => {
  const { t } = useTranslation();
  const {
    form,
    creationStep,
    selectedGroupName,
    setSelectedGroupName,
    groupOptions,
    handleStartAddingElements,
    handleCancelAdding,
    handleSaveAll,
    handleBack,
    isSaving,
    isDiscardModalOpen,
    handleConfirmDiscard,
    handleCancelDiscard,
    isCancelAllModalOpen,
    handleConfirmCancelAll,
    handleCloseCancelAll,
  } = creationModeState;

  const elementCount = useStore(
    form.store,
    (state) => state.values.elements.length,
  );

  const isAddingElements = creationStep === "adding-elements";
  const hasElements = elementCount > 0;

  return (
    <div className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm p-4 overflow-hidden flex flex-col gap-3">
      <div className="w-full">
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleBack}
            startAdornment={<ArrowLeft className="w-4 h-4" />}
            aria-label={t("common.back")}
          />
          <h2 className="text-sm font-semibold text-gray-900">
            {t("smartSurveyor.elementsCreation.title")}
          </h2>
        </div>
        {isAddingElements && hasElements && (
          <CreationModeActionBar
            isMultiAddModeEnabled={isMultiAddModeEnabled}
            onToggleMultiAddMode={onToggleMultiAddMode}
            isEditingElement={isEditingElement}
            onToggleEditMode={onToggleEditMode}
          />
        )}
      </div>
      {!isAddingElements && (
        <GroupSelectionStepV3
          selectedGroupName={selectedGroupName}
          onGroupNameChange={setSelectedGroupName}
          groupOptions={groupOptions}
          onStartAdding={handleStartAddingElements}
        />
      )}
      {isAddingElements && !hasElements && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400">
          <MapPinned className="w-10 h-10 text-blue-500" />
          <p className="text-sm text-gray-500">
            {t("smartSurveyor.elementsCreation.choosePosition")}
          </p>
        </div>
      )}
      {isAddingElements && hasElements && (
        <>
          <ElementsListViewV3 creationModeState={creationModeState} />
          <div className="flex gap-2">
            <Button className="flex-1" onClick={handleCancelAdding}>
              {t("smartSurveyor.elementsCreation.cancelAll")}
            </Button>
            <Button
              className="flex-1"
              variant="primary"
              onClick={handleSaveAll}
              disabled={isSaving}
            >
              {isSaving
                ? t("smartSurveyor.elementsCreation.saving")
                : t("smartSurveyor.elementsCreation.saveAll")}
            </Button>
          </div>
        </>
      )}
      <ConfirmActionModal
        isOpen={isDiscardModalOpen}
        onClose={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        action="delete"
        title={t("smartSurveyor.elementsCreation.discardModal.title")}
        description={t(
          "smartSurveyor.elementsCreation.discardModal.description",
        )}
      />
      <ConfirmActionModal
        isOpen={isCancelAllModalOpen}
        onClose={handleCloseCancelAll}
        onConfirm={handleConfirmCancelAll}
        action="delete"
        title={t("smartSurveyor.elementsCreation.cancelAllModal.title")}
        description={t(
          "smartSurveyor.elementsCreation.cancelAllModal.description",
        )}
      />
    </div>
  );
};
